import { defineStore } from "pinia";
import { FetchService } from "@/services/fetch.service";
import AuthService from "@/services/auth.service";
import { useAuthenticationStore } from "./authentication";
import type { GetSchool } from "@/api_types/teacher";
import { asyncComputed } from "@/utils/asyncComputed";
import type { GetSchoolDetails } from "@/api_types/school";
import dayjs from "dayjs";
import router from "@/router";
// import type { RouteLocationNormalized } from "vue-router";

const school = (): GetSchool | null => (useSchoolStore as any)();

export const useSchoolStore = defineStore("school", {
  state: () => {
    const authStore = useAuthenticationStore();
    return {
      school: null as GetSchool | null,
      account: "",
      schoolDetails: asyncComputed(
        async () => {
          const schoolD = school();
          if (
            schoolD?.school_id &&
            (authStore.jwtData?.authentication_type === "district" ||
              authStore.jwtData?.authentication_type === "school")
          ) {
            return (await FetchService.fetchData(
              "/schools/" + schoolD?.school_id,
              "get"
            )) as GetSchoolDetails;
          }
          return {} as GetSchoolDetails;
        },
        [() => school()?.school_id, () => authStore.jwtData?.authentication_type],
        undefined,
        undefined,
        {},
        false
      ),
      school_id: 0
    };
  },

  actions: {
    async loadSchool(account?: any) {
      if (account) {
        this.school = await FetchService.fetchData("/school/account/" + account, "get");
        // For district schools, set school_id only for specific features
        if (this.school?.school_id) {
          if (this.school?.is_district === "no" || this.school?.is_district === false) {
            // Regular schools - always set the school_id
            this.school_id = this.school.school_id;
            console.log('[SCHOOL STORE] Setting school_id to', this.school_id, 'for regular school');
          } else {
            // District schools - set the school_id ONLY for settings/account pages
            const currentPath = window.location.pathname;
            if (currentPath.includes('/settings') || currentPath.includes('/integration')) {
              this.school_id = this.school.school_id;
              console.log('[SCHOOL STORE] Setting school_id to', this.school_id, 'for district school - SETTINGS/INTEGRATION ONLY');
            } else {
              this.school_id = 0; // Don't set school_id for district schools in other contexts
              console.log('[SCHOOL STORE] District school detected, NOT setting school_id for non-settings page');
            }
          }
        }
      } else this.school = await FetchService.fetchData("/school", "get");
      this.account = account;
      const authStore = useAuthenticationStore();
      this.isExpired();
      if (
        this.school?.school_id &&
        !AuthService.isCorrectSchool(this.school?.school_id) &&
        authStore.me.uid
      ) {
        authStore.logout();
      }
    },
    fetchSchool(account) {
      return this.loadSchool(account);
    },
    isIntegrated() {
      if (this.school != null && this.school?.integration) {
        return this.school?.integration != "none";
      }
    },
    allowRegistration(){
      if (this.school != null && this.school?.integration) {
        return this.school?.integration == "hybrid" || this.school?.integration == 'nycdoe';
      }
    },
    isPasswordUpdatable() {
      if (this.school != null && this.school?.integration) {
        return this.school?.integration == "none" || this.school?.integration == "csv" || this.school?.integration == "hybrid" || this.school?.integration == "nycdoe" || this.school?.integration == "oneroster";
      }
    },
    
    async updateSchool(data: Partial<GetSchoolDetails>) {
      // For district schools on settings pages, make sure we use the correct school_id
      if (this.school?.is_district === 'yes' || this.school?.is_district === true) {
        console.log('[SCHOOL STORE] District school updating settings');
        
        // Make sure we're using the right school_id for district schools
        // If it's 0 but we have a school.school_id, use that for settings pages
        if (!this.school_id && this.school?.school_id) {
          const currentPath = window.location.pathname;
          if (currentPath.includes('/settings') || currentPath.includes('/integration')) {
            console.log('[SCHOOL STORE] Temporarily using school ID for district settings update');
            // Use the actual school ID from the school object directly
            const tempSchoolId = this.school.school_id;
            const allowedFields = [
              'school_contact_name', 'school_contact_email', 'school_contact_phone',
              'school_contact_address1', 'school_contact_city', 'school_contact_state',
              'school_contact_zip', 'school_contact_country', 'timezone'
            ];
            
            // Filter allowed fields for district schools
            const filteredData: any = {};
            Object.keys(data).forEach(key => {
              if (allowedFields.includes(key)) {
                filteredData[key] = data[key];
              } else {
                console.warn(`[SCHOOL STORE] Field "${key}" cannot be updated for district schools, skipping`);
              }
            });
            
            // Make direct API call without changing store.school_id
            const res = await FetchService.fetchData("/schools/" + tempSchoolId, "put", filteredData);
            if (res?.school_id) {
              this.schoolDetails.data = res;
              Object.keys(this.school || {}).forEach((key) => {
                if (key in res) this.school![key] = res[key];
              });
              return true;
            }
            return false;
          }
        }
        
        // If it's a settings page but school_id is already set correctly, allow normally with filtering
        if (this.school_id) {
          const allowedFields = [
            'school_contact_name', 'school_contact_email', 'school_contact_phone',
            'school_contact_address1', 'school_contact_city', 'school_contact_state',
            'school_contact_zip', 'school_contact_country', 'timezone'
          ];
          
          // Filter allowed fields for district schools
          const filteredData: any = {};
          Object.keys(data).forEach(key => {
            if (allowedFields.includes(key)) {
              filteredData[key] = data[key];
            } else {
              console.warn(`[SCHOOL STORE] Field "${key}" cannot be updated for district schools, skipping`);
            }
          });
          
          data = filteredData;
        }
      }
      
      // Safety check - only allow updates if there's a valid school_id
      if (!this.school_id && !(this.school?.is_district && this.school?.school_id)) {
        console.error('[SCHOOL STORE] Cannot update school: No valid school_id');
        return false;
      }
      
      // Normal update flow for regular schools or district schools with set school_id
      const res = await FetchService.fetchData("/schools/" + this.school_id, "put", data);
      if (res?.school_id) {
        this.schoolDetails.data = res;
        Object.keys(this.school || {}).forEach((key) => {
          if (key in res) this.school![key] = res[key];
        });
        return true;
      }
      return false;
    },
    isExpired() {
      const authStore = useAuthenticationStore();
      if (
        (authStore.level === "district" || authStore.level === "school") &&
        this.school?.expiration_date
      ) {
        // const name = (to || router.currentRoute.value).name as string;
        const date = dayjs(this.school.expiration_date);
        const today = dayjs(dayjs().format("YYYY-MM-DD"));
        if (date < today) {
          return true;        
        }else{
          return false;
        }
      }
      return false;
    },
    isDistrict() {
      const val = this.schoolDetails?.data?.is_district;
      return val === "yes" || val === true;
    },
  }
});
